/**
 * 卸コンソールのマスタ管理画面（C-18 便・C-19 カタログ）で共有するフォームの見た目。
 *
 * **生の色値を書かない＝semantic トークンのみ参照**（05 §7）。見た目の正は Claude Design（D-45）で、
 * 確定したら `packages/ui/tokens.css` の差し替えだけで全画面に効く。
 * `packages/ui` へ昇格させると 3 SPA すべてに影響するため、console 内の共有に留めている。
 */

export const inputStyle = {
  font: "inherit",
  minHeight: 44, // タップ44px（05 §5.3）
  padding: "0 var(--kz-space-3)",
  border: "1px solid var(--kz-color-border)",
  borderRadius: "var(--kz-radius-sm)",
  background: "var(--kz-color-surface)",
} as const;

export const labelStyle = {
  display: "grid",
  gap: "var(--kz-space-1)",
  fontSize: "var(--kz-font-size-sm)",
  fontWeight: 600,
} as const;

export const hintStyle = {
  fontSize: "var(--kz-font-size-sm)",
  fontWeight: 400,
  color: "var(--kz-color-ink-muted)",
} as const;

/*
 * ★**ボタンの style オブジェクトはここに置かない**（M1.7・S3）。
 *
 * 以前は `primaryButton`（44px）と `ghostButton`（36px）をここから配っていたが、
 * それとは別に `.kz-btn`（packages/ui）・`Inbox.tsx` の `manualOrderLink` / `rowActionButton`・
 * `OrderDetail.tsx` の `ghost` が並存し、**同じ画面の中でボタンの高さが 36/40/44px と
 * 揃わない**状態になっていた。加えて inline style では `:hover` / `:active` が書けず、
 * 押しても何も返さないボタンが残っていた。
 *
 * → 共有の `.kz-btn` 系（`--primary` / `--flat` / `--danger` / `--sm`）へ寄せた。
 *   使い分け：既定＝枠線（副操作）／`--primary`＝その画面の主操作／`--flat`＝一覧の主要導線・
 *   行内の進行操作／`--sm`＝**表の行の中**だけ／`--accent`（黄）＝**承認専用**（LC-02）。
 *   スマホ幅の 44px は `mobile.css` がまとめて面倒を見る。
 */

export const overlayStyle = {
  position: "fixed",
  inset: 0,
  background: "var(--kz-color-scrim)",
  // M1.7＝共有の `Modal`/`ConfirmDialog` と同じ覆い方に揃える（背景を少しぼかす・同じ層に置く）。
  // ここだけ据え置くと、同じ画面の中で「確認ダイアログはぼける／編集モーダルはぼけない」になる。
  backdropFilter: "blur(4px)",
  WebkitBackdropFilter: "blur(4px)",
  display: "grid",
  placeItems: "center",
  padding: "var(--kz-space-4)",
  zIndex: 50,
  overflowY: "auto",
} as const;

/** 「このままだと業務が回らない」状態を出す帯（例：利用できる便がゼロ）。 */
export const warnBanner = {
  margin: "0 0 var(--kz-space-4)",
  padding: "var(--kz-space-3) var(--kz-space-4)",
  borderRadius: "var(--kz-radius-sm)",
  background: "var(--kz-status-warn-weak)",
  border: "1px solid var(--kz-color-border)",
} as const;

/**
 * U10：**担当者（staff）にできないこと**を、その機能の画面に書く。
 *
 * console の作法は「押せないボタンを出さない」（403 になるだけの操作を見せない）だが、
 * それだけだと**一覧にデータがある画面では追加ボタンが黙って消えるだけ**で、
 * 担当者からは「この画面では何もできない」ようにしか見えない（stg 一巡 U10）。
 * 一言そえて「できないのは自分の権限のせい」と分かるようにする。
 */
export const adminOnlyNote = (what: string) => `（${what}は管理者のみ行えます）`;
